import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import hj from "@hotjar/browser";
import { type Contact } from "@umami/chains";
import { useDynamicDrawerContext, useDynamicModalContext } from "@umami/components";
import { useSortedContacts } from "@umami/state";

import { DeleteContactModal } from "./DeleteContactModal";
import { EditContactMenu } from "./EditContactMenu";
import { EditIcon, ThreeDotsIcon, TrashIcon } from "../../../assets/icons";
import { useColor } from "../../../styles/useColor";
import { ActionsDropdown } from "../../ActionsDropdown";
import { CopyAddressButton } from "../../CopyAddressButton";
import { EmptyMessage } from "../../EmptyMessage";
import { DrawerContentWrapper } from "../DrawerContentWrapper";

type ContactItemProps = {
  contact: Contact;
};

const ContactItem = ({ contact }: ContactItemProps) => {
  const { openWith: openDrawer } = useDynamicDrawerContext();
  const { openWith: openModal } = useDynamicModalContext();
  const color = useColor();

  const actions = (
    <Box fontSize="16px">
      <Button
        data-testid="edit-network"
        onClick={e => {
          e.stopPropagation();

          return openDrawer(<EditContactMenu contact={contact} />);
        }}
        variant="dropdownOption"
      >
        <EditIcon />
        <Text color={color("900")} fontWeight="600">
          Edit
        </Text>
      </Button>
      <Button
        data-testid="remove-network"
        onClick={() => openModal(<DeleteContactModal contact={contact} />)}
        variant="dropdownOption"
      >
        <TrashIcon />
        <Text color={color("900")} fontWeight="600">
          Remove
        </Text>
      </Button>
    </Box>
  );

  return (
    <Center
      key={contact.pkh}
      alignItems="center"
      justifyContent="space-between"
      width="full"
      height="60px"
      data-testid="peer-row"
    >
      <Flex height="100%">
        <Center alignItems="flex-start" flexDirection="column" gap="6px">
          <Box>
            <Flex alignItems="center" gap="6px">
              <Heading color={color("900")} size="lg">
                {contact.name}
              </Heading>
            </Flex>
          </Box>
          <CopyAddressButton address={contact.pkh} isLong={false} />
        </Center>
      </Flex>
      <ActionsDropdown actions={actions}>
        <IconButton
          color={color("500")}
          transform="rotate(90deg)"
          aria-label="More Options"
          icon={<ThreeDotsIcon />}
          title="More Options"
          variant="iconButtonSolid"
        />
      </ActionsDropdown>
    </Center>
  );
};

export const AddressBookMenu = () => {
  const { openWith } = useDynamicDrawerContext();
  const contacts = useSortedContacts();
  hj.stateChange("menu/address_book_menu");

  return (
    <DrawerContentWrapper
      actions={
        <Button
          width="fit-content"
          marginTop="18px"
          padding="0 24px"
          onClick={() => openWith(<EditContactMenu />)}
          variant="primary"
        >
          Add contact
        </Button>
      }
      title="Address book"
    >
      {contacts.length ? (
        <VStack
          alignItems="flex-start"
          gap="24px"
          marginTop="24px"
          divider={<Divider />}
          spacing="0"
        >
          {contacts.map(contact => (
            <ContactItem key={contact.pkh} contact={contact} />
          ))}
        </VStack>
      ) : (
        <EmptyMessage
          alignItems="flex-start"
          marginTop="40px"
          subtitle="Your Contacts will appear here..."
          title="No Contacts to show"
        />
      )}
    </DrawerContentWrapper>
  );
};
